import { useCallback, useEffect, useRef, useState } from 'react';

import type { PlaylistSection } from '@/lib/audio/playlist-manager';
import { PlaylistManager } from '@/lib/audio/playlist-manager';

export interface AudioSectionInput {
  position: number;
  audioUrl: string | null;
  audioDurationMs: number | null;
  generationStatus: string;
  estimatedDurationMs: number | null;
}

export interface UseAudioPlayerReturn {
  isReady: boolean;
  isPlaying: boolean;
  currentTimeMs: number;
  totalDurationMs: number;
  currentSectionIndex: number;
  sectionProgress: number;
  play: () => void;
  pause: () => void;
  seek: (timeMs: number) => void;
  togglePlayPause: () => void;
  setPlaybackRate: (rate: number) => void;
  setVolume: (volume: number) => void;
  seekToSection: (index: number) => void;
  getAudioElements: () => [HTMLAudioElement, HTMLAudioElement] | null;
}

function toPlaylistSections(sections: AudioSectionInput[]): PlaylistSection[] {
  return sections.map((s) => ({
    url: s.audioUrl,
    durationMs: s.audioDurationMs ?? s.estimatedDurationMs ?? 0,
  }));
}

export function useAudioPlayer(sections: AudioSectionInput[]): UseAudioPlayerReturn {
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTimeMs, setCurrentTimeMs] = useState(0);
  const [totalDurationMs, setTotalDurationMs] = useState(0);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [sectionProgress, setSectionProgress] = useState(0);

  const playlistRef = useRef<PlaylistManager | null>(null);

  useEffect(() => {
    const playlistSections = toPlaylistSections(sections);
    const playlist = new PlaylistManager(playlistSections);
    playlistRef.current = playlist;

    setTotalDurationMs(playlist.totalDurationMs);

    playlist.onStateChange((state) => {
      setIsPlaying(state === 'playing');
    });

    playlist.onTimeUpdate((timeMs) => {
      setCurrentTimeMs(timeMs);
      setSectionProgress(playlist.sectionProgress);
    });

    playlist.onSectionChange((index) => {
      setCurrentSectionIndex(index);
    });

    setIsReady(true);

    return () => {
      playlist.destroy();
      playlistRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const play = useCallback(() => {
    playlistRef.current?.play();
  }, []);

  const pause = useCallback(() => {
    playlistRef.current?.pause();
  }, []);

  const seek = useCallback((timeMs: number) => {
    playlistRef.current?.seek(timeMs);
  }, []);

  const togglePlayPause = useCallback(() => {
    if (playlistRef.current?.isPlaying) {
      playlistRef.current.pause();
    } else {
      playlistRef.current?.play();
    }
  }, []);

  const setPlaybackRate = useCallback((rate: number) => {
    playlistRef.current?.setPlaybackRate(rate);
  }, []);

  const setVolume = useCallback((volume: number) => {
    playlistRef.current?.setVolume(volume);
  }, []);

  const seekToSection = useCallback((index: number) => {
    playlistRef.current?.seekToSection(index);
  }, []);

  const getAudioElements = useCallback((): [HTMLAudioElement, HTMLAudioElement] | null => {
    return playlistRef.current?.getAudioElements() ?? null;
  }, []);

  return {
    isReady,
    isPlaying,
    currentTimeMs,
    totalDurationMs,
    currentSectionIndex,
    sectionProgress,
    play,
    pause,
    seek,
    togglePlayPause,
    setPlaybackRate,
    setVolume,
    seekToSection,
    getAudioElements,
  };
}
